import PropTypes from "prop-types";
import React, { useState, useContext } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import SupportIcon from "@mui/icons-material/HelpOutline";
import DashboardIcon from "@mui/icons-material/HomeOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import LearnIcon from "../../components/Icons/LearnIcon";
import PerformanceIcon from "../../components/Icons/PerformanceIcon";
import SideNav from "../../components/Nav/SideNav/SideNav";
import {
  dashboard,
  learn,
  performance,
  settings,
  performanceAchievements,
  performanceLeaderboard,
  learnLearningPath,
  learnLibrary,
  apps
} from "../../routes/LearningCenterRoutes";

import AppContext from "../../context/AppContext";
const sideNavRoutes = {
  todo: {
    title: "sideNav.todo",
    icon: <ChatOutlinedIcon />,
    section: "upper"
  },
  dashboard: {
    path: dashboard,
    title: "sideNav.home",
    icon: <DashboardIcon />,
    section: "upper"
  },
  learn: {
    title: "sideNav.learn",
    icon: <LearnIcon />,
    section: "upper",
    path: learn,
    children: [
      {
        path: learnLearningPath,
        title: "sideNav.assignments",
        permission: "allowLearningPathAssignments"
      },
      {
        path: learnLibrary,
        title: "sideNav.library",
        permission: "allowLibraryAccess"
      }
    ],
    expandable: true
  },
  performance: {
    path: performance,
    title: "sideNav.performance",
    icon: <PerformanceIcon />,
    section: "upper",
    children: [
      {
        path: performanceLeaderboard,
        title: "sideNav.leaderboard",
        permission: "allowPerformanceLeaderboard"
      }, //it's just the nav option.  we don't have a UI for this page currently (ticket#58)
      {
        path: performanceAchievements,
        title: "sideNav.achievements"
      }
    ],
    expandable: true
  },
  apps: {
    path: apps,
    title: "sideNav.apps",
    icon: <AppsIcon />,
    section: "upper"
  },
  settings: {
    path: settings,
    title: "sideNav.settings",
    icon: <SettingsIcon />,
    section: "lower",
    permission: "isAdmin"
  },
  support: {
    title: "sideNav.support",
    icon: <SupportIcon />,
    section: "lower"
  }
};

const permissionsFilter = (item, userPermissions) =>
  !item.permission || userPermissions[item.permission];

const filterChildren = (menuFolder, userPermissions) => {
  return {
    ...menuFolder,
    children: menuFolder.children.filter(item =>
      permissionsFilter(item, userPermissions)
    )
  };
};

const getSideNavRoutes = userPermissions => {
  const {
    todo,
    dashboard,
    learn,
    performance,
    apps,
    settings,
    support
  } = sideNavRoutes;

  let routes = [];
  routes.push(todo, dashboard);
  if (
    userPermissions.allowLearningPathAssignments ||
    userPermissions.allowLibraryAccess
  ) {
    routes.push(filterChildren(learn, userPermissions));
  }
  routes.push(filterChildren(performance, userPermissions));
  routes.push(apps);
  if (userPermissions.isAdmin) {
    routes.push(settings);
  }
  routes.push(support);
  return routes;
};

const LearningCenterSideNav = ({
  currentPath,
  onSupportClick,
  logo,
  userPermissions
}) => {
  const {
    mobileNavbarOpen,
    setMobileNavbarOpen,
    setWelcomeSlideOutOpen
  } = useContext(AppContext);

  const [sideNavExpanded, setSideNavExpanded] = useState(false);

  const dispatch = useDispatch();
  const handleExpandSideNav = expanded => {
    setSideNavExpanded(expanded);
    setWelcomeSlideOutOpen(false);
  };

  const handleMenuNavItemClick = itemClicked => {
    setWelcomeSlideOutOpen(itemClicked.title === "sideNav.todo");
    if (itemClicked.title === "sideNav.support") {
      onSupportClick();
    } else {
      if (itemClicked.title !== "sideNav.todo") {
        dispatch(push(itemClicked.path));
      }
      setSideNavExpanded(false);
      setMobileNavbarOpen(!mobileNavbarOpen);
    }
  };
  const routes = getSideNavRoutes(userPermissions);

  return (
    <SideNav
      onExpandSideNav={handleExpandSideNav}
      expanded={sideNavExpanded}
      routes={routes}
      mobileNavbarOpen={mobileNavbarOpen}
      onMenuNavItemClick={handleMenuNavItemClick}
      logo={logo}
      hideDivider
      currentPath={currentPath}
    />
  );
};

LearningCenterSideNav.propTypes = {
  currentPath: PropTypes.string,
  userPermissions: PropTypes.shape({
    isAdmin: PropTypes.bool
  }),
  logo: PropTypes.string,
  onSupportClick: PropTypes.func
};

export default LearningCenterSideNav;
